import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Brain, Shield, Plus, Download, Wrench, BarChart3, Hammer, ChevronLeft, PieChart, Database } from 'lucide-react';
import UserManagement from './UserManagement';
import '../../tailwindcss/Tailwind.css';
import { buildApiUrl } from '../../services/apiConfig';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [metrics, setMetrics] = useState({
    totalPrompts: 0,
    totalCost: 0,
    avgLatency: 0,
    avgTokens: 0
  });
  const [modelCount, setModelCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState();

  useEffect(() => {
    const storedUserId = sessionStorage.getItem('user_id');
    setUserId(storedUserId || 'U1757535255');

    const userRole = sessionStorage.getItem('role');
    setIsAdmin(userRole === 'admin');
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchMetrics();
    }
  }, [isAdmin]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);

      // Fetch summary metrics
      const summaryResponse = await fetch(buildApiUrl('AUDIT', '/responsible-ai/summary-metrics'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dateFilter: {
            type: '24h' // Get last 24 hours data
          },
          searchTerm: '',
          searchField: 'all'
        }),
      });
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setMetrics(summaryData);
      }

      // Fetch model usage to count active models
      const modelResponse = await fetch(buildApiUrl('AUDIT', '/responsible-ai/model-usage'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dateFilter: {
            type: '24h'
          },
          searchTerm: '',
          searchField: 'all'
        }),
      });

      if (modelResponse.ok) {
        const modelData = await modelResponse.json();
        setModelCount(Object.keys(modelData).length);
      }

    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return Math.round(num).toString();
  };

  const formatLatency = (latency) => {
    return latency ? `${latency.toFixed(1)}s` : '0s';
  };

  const handleCustomBuildClick = () => {
    navigate('/custom-build');
  };

  // Only used for admin view
  const handleResponsibleAIClick = () => {
    navigate('/responsible-ai');
  };

  const handleGuardrailManagerClick = () => {
    navigate('/guardrail');
  };

  const handleCustomerPoolManagerClick = () => {
    navigate('/cust-group-management');
  }

  const handleCustomerSegmentationClick = () => {
    navigate('/segmentation');
  };

  const handleMetadataExtractionClick = () => {
    navigate('/metadata-extraction');
  };

  // Custom Build Card - shown to all users
  const CustomBuildCard = () => (
    <div
      className="admin-card bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={handleCustomBuildClick}
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="bg-gray-100 p-3 rounded-full">
            <Wrench className="w-6 h-6 text-primary" />
          </div>
          <span className="bg-secondary text-white text-xs font-semibold px-3 py-1 rounded-full uppercase">
            AI Tools
          </span>
        </div>

        <div className="mb-4">
          <h3 className="text-xl font-semibold text-primary mb-2">Custom Build</h3>
          <p className="text-gray-600 leading-relaxed">
            Build and manage intelligent agents for your business
          </p>
        </div>

        <div className="flex gap-3">
          <button className="bg-secondary hover:bg-secondaryDark text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors text-sm">
            <Hammer className="w-4 h-4 " />
            Build Agents
          </button>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'users':
        return <UserManagement />;

      case 'ai':
        return (
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg text-primary font-semibold mb-4">AI Configuration</h3>
            <p className="text-gray-600">AI settings and model configurations will be available here.</p>
          </div>
        );

      case 'overview':
      default:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Advisor Admins Card
            <div
              className="admin-card bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="bg-gray-100 p-3 rounded-full">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <span className="bg-secondary text-white text-xs font-semibold px-3 py-1 rounded-full uppercase">
                    Admin
                  </span>
                </div>

                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-primary mb-2">Advisor Admins</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Add and manage users (advisors and administrators). Control access permissions and assign roles to staff members.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button className="bg-secondary hover:bg-secondaryDark text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveTab('users');
                    }}
                  >
                    <Plus className="w-4 h-4" />
                    Create New User
                  </button>
                </div>
              </div>
            </div> */}

            {/* Responsible AI Card */}
            <div
              className="admin-card bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={handleResponsibleAIClick}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="bg-gray-100 p-3 rounded-full">
                    <Brain className="w-6 h-6 text-primary" />
                  </div>
                  <span className="bg-secondary text-white text-xs font-semibold px-3 py-1 rounded-full uppercase">
                    AI Monitoring
                  </span>
                </div>

                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-primary mb-2">Responsible AI & Monitoring</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Monitor AI performance metrics, token usage, response latency, and ethical AI practices.
                  </p>
                </div>

                {loading ? (
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <span className="bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded">Loading...</span>
                    <span className="bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded">Loading...</span>
                    <span className="bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded">Loading...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <span className="bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded">
                      Avg Latency: {formatLatency(metrics.avgLatency)}
                    </span>
                    <span className="bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded">
                      Daily Tokens: {formatNumber(metrics.avgTokens)}
                    </span>
                    <span className="bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded">
                      Active Models: {modelCount}
                    </span>
                  </div>
                )}

                <div className="flex gap-3">
                  <button className="bg-secondary hover:bg-secondaryDark text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors text-sm">
                    <BarChart3 className="w-4 h-4" />
                    View Metrics
                  </button>
                  <button
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchMetrics();
                    }}
                  >
                    <Download className="w-4 h-4" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Guardrails Manager Card */}
            <div
              className="admin-card bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={handleGuardrailManagerClick}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="bg-gray-100 p-3 rounded-full">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <span className="bg-secondary text-white text-xs font-semibold px-3 py-1 rounded-full uppercase">
                    Security
                  </span>
                </div>

                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-primary mb-2">Guardrails Manager</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Manage AI safety guardrails, content filtering rules, response boundaries, and compliance monitoring.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button className="bg-secondary hover:bg-secondaryDark text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors text-sm">
                    <Shield className="w-4 h-4 " />
                    Manage Rules
                  </button>
                </div>
              </div>
            </div>

            {/* Customer Pool Management */}
            {/* <div
              className="admin-card bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={handleCustomerPoolManagerClick}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="bg-gray-100 p-3 rounded-full">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <span className="bg-secondary text-white text-xs font-semibold px-3 py-1 rounded-full uppercase">
                    Customer Pools
                  </span>
                </div>

                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-primary mb-2">Customer & Pool Manager</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Manage customer pool, segment users, and assign specific AI interaction rules based on customer profiles.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button className="bg-secondary hover:bg-secondaryDark text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors text-sm">
                    <Shield className="w-4 h-4 " />
                    Add Groups
                  </button>
                </div>
              </div>
            </div> */}

            {/* Custom AI Build Management */}
            {/* <CustomBuildCard /> */}

            {/* Customer Segmentation Analysis */}
            {/* <div className="admin-card bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={handleCustomerSegmentationClick}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="bg-gray-100 p-3 rounded-full">
                    <PieChart className="w-6 h-6 text-primary" />
                  </div>
                  <span className="bg-secondary text-white text-xs font-semibold px-3 py-1 rounded-full uppercase">
                    Analytics
                  </span>
                </div>
                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-primary mb-2">Customer Segmentation</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Analyze customer data to identify segments, patterns, and anomalies to drive targeted business strategies.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button className="bg-secondary hover:bg-secondaryDark text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors text-sm">
                    <PieChart className="w-4 h-4" /> Analyze Data
                  </button>
                </div>
              </div>
            </div> */}

            {/* Metadata Extraction Tool */}
            {/* <div
              className="admin-card bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={handleMetadataExtractionClick}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="bg-gray-100 p-3 rounded-full">
                    <Database className="w-6 h-6 text-primary" />
                  </div>
                  <span className="bg-secondary text-white text-xs font-semibold px-3 py-1 rounded-full uppercase">
                    Data Tools
                  </span>
                </div>

                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-primary mb-2">Neo4j to SQL Metadata Extraction</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Extract metadata from Neo4j graph databases and generate SQL queries based on natural language questions.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button className="bg-secondary hover:bg-secondaryDark text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors text-sm">
                    <Database className="w-4 h-4" />
                    Access Tool
                  </button>
                </div>
              </div>
            </div> */}
          </div>
        );
    }
  };

  // Render different views based on admin status
  if (!isAdmin) {
    // Non-admin view - only show the Custom Build card
    return (
      <div className="settings-page bg-gray-50 min-h-screen p-6">
        <div className="max-w-full mx-auto">
          {/* Simple header for non-admin */}
          <div className="bg-white p-6 rounded-xl shadow-md mb-6">
            <div className="flex items-center gap-3 mb-4">
              <button
                className="text-gray-600 hover:text-gray-800 transition-colors"
                onClick={() => navigate('/home')}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div className="text-left">
                <h1 className="text-2xl font-medium text-primary">Build Tools</h1>
              </div>
            </div>
          </div>

          {/* Only show Custom Build card */}
          <div className="">
            <CustomBuildCard />
          </div>
        </div>
      </div>
    );
  }

  // Admin view - show everything
  return (
    <div className="settings-page bg-gray-50 min-h-screen p-6">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-6">
          <div className="flex items-center gap-3 mb-8">
            <button
              className="text-gray-600 hover:text-gray-800 transition-colors"
              onClick={() => navigate('/home')}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div className="text-left">
              <h1 className="text-4xl font-medium text-primary mb-2">Admin Settings</h1>
              <p className="text-gray-600 text-sm">Manage system administrators, AI configurations, and guardrails</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 justify-center">
              <button
                className={`tab-button py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview'
                  ? 'border-secondary text-secondaryDark'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              {/* <button
                className={`tab-button py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'users'
                  ? 'border-secondary text-secondaryDark'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                onClick={() => setActiveTab('users')}
              >
                User Management
              </button> */}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

export default SettingsPage;